// Globe.tsx — the 'globe' decorative media variant.
//
// Extracted VERBATIM from the old PostMedia.tsx. An SVG ornament on seeded demo posts, not a
// photograph: aria-hidden, never interactive, never opens the viewer.

import styled, { useTheme } from 'styled-components';

const GlobeWrapper = styled.div<{ $fill?: boolean }>`
  width: 100%;
  height: ${({ $fill }) => ($fill ? '100%' : 'auto')};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  line-height: 0;

  /* In fill mode the SVG covers the (square) tile; preserveAspectRatio="slice"
     keeps the globe centred and clips the wider edges. */
  & > svg {
    display: block;
    width: 100%;
    height: ${({ $fill }) => ($fill ? '100%' : 'auto')};
  }
`;

export function Globe({ fill = false }: { fill?: boolean }) {
  const theme = useTheme();
  return (
    <GlobeWrapper $fill={fill} data-testid="post-media-globe" aria-hidden="true">
      <svg
        viewBox="0 0 480 200"
        width="100%"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="pmg-glow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor={`${theme.color.brandFrom}8c`} />
            <stop offset="100%" stopColor={`${theme.color.brandFrom}00`} />
          </radialGradient>
          <linearGradient id="pmg-sphere" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={theme.color.brandFrom} />
            <stop offset="100%" stopColor={theme.color.brandTo} />
          </linearGradient>
          <radialGradient id="pmg-gloss" cx="38%" cy="35%" r="45%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>

        {/* Background — follows bgRoot so light theme doesn't stay near-black */}
        <rect width="480" height="200" fill={theme.color.bgRoot} />

        {/* Ambient glow halo */}
        <circle cx="240" cy="100" r="110" fill="url(#pmg-glow)" />

        {/* Globe sphere */}
        <circle cx="240" cy="100" r="72" fill="url(#pmg-sphere)" opacity="0.90" />

        {/* Latitude / longitude grid lines */}
        <g stroke="rgba(190,210,255,0.40)" strokeWidth="0.8" fill="none">
          <ellipse cx="240" cy="100" rx="72" ry="72" />
          <ellipse cx="240" cy="100" rx="72" ry="26" />
          <ellipse cx="240" cy="100" rx="72" ry="52" />
          <ellipse cx="240" cy="100" rx="26" ry="72" />
          <ellipse cx="240" cy="100" rx="52" ry="72" />
        </g>

        {/* Gloss highlight */}
        <circle cx="240" cy="100" r="72" fill="url(#pmg-gloss)" />

        {/* Outer glow ring */}
        <circle
          cx="240"
          cy="100"
          r="76"
          fill="none"
          stroke={`${theme.color.brandFrom}59`}
          strokeWidth="6"
        />
      </svg>
    </GlobeWrapper>
  );
}
